import { useContext, useEffect, useRef } from "react";
import type { ComponentProps } from "react";
import { DialogPopup } from "@salestrak/ui";
import {
  IssueViewBoundaryContext,
  IssueViewContext,
} from "./use-issue-view";
import { IssueViewDialogStateContext } from "./issue-view-dialog-state";
import { IssueViewDialog } from "./issue-view-dialog";
import { DetailStackHost } from "#app/components/detail-view/detail-stack";

/**
 * Scope project opens to an existing dialog. The project renders as a nested
 * fullscreen dialog in this subtree, so Base UI owns modal stacking and focus
 * restoration. Closing or unmounting the owner closes the nested project.
 */
export function IssueViewDialogBoundaryPopup({
  ownerOpen,
  children,
  ...props
}: Omit<ComponentProps<typeof DialogPopup>, "ref"> & { ownerOpen: boolean }) {
  const issueView = useContext(IssueViewContext);
  // Like IssueViewContext above, the dialog-state provider is a workspace-
  // layout concern: a host mounted outside it has no contextual project opens,
  // so the nested dialog has nothing to render there.
  const dialogState = useContext(IssueViewDialogStateContext);
  const closeIssue = issueView?.closeIssue;
  const wasOpen = useRef(ownerOpen);
  const openRef = useRef(ownerOpen);
  openRef.current = ownerOpen;

  useEffect(() => {
    if (wasOpen.current && !ownerOpen) closeIssue?.();
    wasOpen.current = ownerOpen;
  }, [closeIssue, ownerOpen]);

  useEffect(
    () => () => {
      if (openRef.current) closeIssue?.();
    },
    [closeIssue],
  );

  return (
    <IssueViewBoundaryContext.Provider value>
      <DialogPopup {...props}>
        {/* A project opened from inside this dialog nests over it; the
          * detail drawers standing beneath the dialog are not what the
          * click was about, so the open pushes rather than clearing them. */}
        <DetailStackHost>
          {children}
          {dialogState ? <IssueViewDialog nested /> : null}
        </DetailStackHost>
      </DialogPopup>
    </IssueViewBoundaryContext.Provider>
  );
}
